import React, { useState, useMemo, useCallback } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { ToggleButton } from "primereact/togglebutton";

import { GenericDetailsModal, Table } from "@components";
import { useAllContracts, useContractDetail } from "@api";

import { getContractDetailsSections } from "./getContractDetailsSections";

import { ContractRow } from "@types";
import { formatDate } from "@utils";
import { STRING } from "@constants";

const T = STRING.CONTRACT_DETAILS;

// --- Flat row type for fast sorting ---

interface FlatRow {
  contractId: string;
  contractNumber: string;
  customerName: string;
  unitName: string;
  locationName: string;
  currentRate: string;
  balance: string;
  paidThrough: string;
  unitStatus: string;
  contractOrigin: string;
  movedIn: string;
}

function flattenRow(row: ContractRow): FlatRow {
  const c = row.contract;
  const e = row.entity;
  return {
    contractId: c?.contractId || "",
    contractNumber: c?.contractNumber || "",
    customerName: e
      ? `${e.firstName || ""} ${e.lastName || ""}`.trim() || "-"
      : "-",
    unitName: c?.unitName || "",
    locationName: row.location?.locationName || "",
    currentRate: c?.currentRate || "",
    balance: c?.balance || "",
    paidThrough: c?.paidThrough || "",
    unitStatus: c?.unitStatus || "",
    contractOrigin: c?.contractOrigin || "",
    movedIn: c?.movedIn || "",
  };
}

// --- Filter options ---

const STATUS_OPTIONS = [
  { label: "All Statuses", value: "ALL" },
  { label: "Occupied", value: "Occupied" },
  { label: "Reserved", value: "Reserved" },
  { label: "Rented", value: "Rented" },
  { label: "Delinquent", value: "Delinquent" },
];

// --- Helpers ---

const fmtCurrency = (val: string) => {
  if (!val) return "-";
  const num = parseFloat(val);
  return isNaN(num) || num === 0 ? "-" : `$${num.toFixed(2)}`;
};

const STATUS_SEVERITY: Record<
  string,
  "success" | "warning" | "info" | "danger"
> = {
  Occupied: "success",
  Reserved: "warning",
  Rented: "info",
  Delinquent: "danger",
};

// --- Column definitions (flat fields — fast sorting) ---

const COLUMNS = [
  {
    field: "contractNumber",
    header: "Contract #",
    sortable: true,
    body: (row: FlatRow) => row.contractNumber || "-",
  },
  {
    field: "customerName",
    header: "Customer",
    sortable: true,
  },
  {
    field: "unitName",
    header: "Unit",
    sortable: true,
    body: (row: FlatRow) => row.unitName || "-",
  },
  {
    field: "locationName",
    header: "Location",
    sortable: true,
    body: (row: FlatRow) => row.locationName || "-",
  },
  {
    field: "currentRate",
    header: "Rate",
    sortable: true,
    body: (row: FlatRow) => fmtCurrency(row.currentRate),
  },
  {
    field: "balance",
    header: "Balance",
    sortable: true,
    body: (row: FlatRow) => {
      const formatted = fmtCurrency(row.balance);
      if (formatted !== "-" && parseFloat(row.balance) > 0) {
        return <Tag severity="danger" value={formatted} />;
      }
      return formatted;
    },
  },
  {
    field: "paidThrough",
    header: "Paid Through",
    sortable: true,
    body: (row: FlatRow) => formatDate(row.paidThrough),
  },
  {
    field: "unitStatus",
    header: "Status",
    sortable: true,
    body: (row: FlatRow) => {
      if (!row.unitStatus) return "-";
      return <Tag value={row.unitStatus} severity={STATUS_SEVERITY[row.unitStatus]} />;
    },
  },
  {
    field: "contractOrigin",
    header: "Origin",
    sortable: true,
    body: (row: FlatRow) => {
      const isPlatform = row.contractOrigin === "PLATFORM";
      return (
        <Tag
          value={isPlatform ? "Platform" : "SC"}
          severity={isPlatform ? "success" : undefined}
        />
      );
    },
  },
  {
    field: "movedIn",
    header: "Moved In",
    sortable: true,
    body: (row: FlatRow) => formatDate(row.movedIn),
  },
];

// --- Main Component ---

export function Contracts() {
  const [originFilter, setOriginFilter] = useState("PLATFORM");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [globalFilter, setGlobalFilter] = useState("");

  const apiStatus = statusFilter !== "ALL" ? statusFilter : undefined;

  const { data, loading, loadMore, hasMore } = useAllContracts(
    undefined,
    originFilter || undefined,
    apiStatus,
  );

  const [selectedContractId, setSelectedContractId] = useState<string | null>(
    null,
  );
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);

  const { contract: contractDetail, loading: detailLoading } =
    useContractDetail(isDetailsModalOpen ? selectedContractId : null);

  const sections = useMemo(
    () =>
      isDetailsModalOpen && contractDetail
        ? getContractDetailsSections(contractDetail).sections
        : [],
    [isDetailsModalOpen, contractDetail],
  );

  // Flatten data for fast sorting + client-side search
  const flatData = useMemo(() => {
    let rows = data.map(flattenRow);
    if (globalFilter) {
      const q = globalFilter.toLowerCase();
      rows = rows.filter(
        (r) =>
          r.contractNumber.toLowerCase().includes(q) ||
          r.customerName.toLowerCase().includes(q) ||
          r.unitName.toLowerCase().includes(q) ||
          r.locationName.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [data, globalFilter]);

  const handleRowClick = useCallback((rowData: { data: FlatRow }) => {
    setSelectedContractId(rowData.data.contractId);
    setIsDetailsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsDetailsModalOpen(false);
    setSelectedContractId(null);
  }, []);

  const hasActiveFilters =
    originFilter !== "PLATFORM" || statusFilter !== "ALL" || globalFilter;

  const filterBar = useMemo(
    () => (
      <div className="flex flex-wrap align-items-center gap-2">
        <ToggleButton
          checked={originFilter === "PLATFORM"}
          onChange={(e) => setOriginFilter(e.value ? "PLATFORM" : "")}
          onLabel="Platform Only"
          offLabel="All Sources"
          onIcon="pi pi-check"
          offIcon="pi pi-globe"
          className="p-button-sm"
        />
        <Dropdown
          value={statusFilter}
          options={STATUS_OPTIONS}
          onChange={(e) => setStatusFilter(e.value)}
          placeholder="Status"
          className="p-inputtext-sm"
          style={{ minWidth: "155px" }}
        />
        <div
          className="flex-grow-1"
          style={{ maxWidth: "320px", minWidth: "200px" }}
        >
          <IconField iconPosition="left" className="w-full">
            <InputIcon className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder="Search by name, unit, contract #..."
              className="p-inputtext-sm w-full"
            />
          </IconField>
        </div>
        {hasActiveFilters && (
          <Button
            label="Reset"
            icon="pi pi-filter-slash"
            className="p-button-text p-button-sm p-button-secondary"
            onClick={() => {
              setOriginFilter("PLATFORM");
              setStatusFilter("ALL");
              setGlobalFilter("");
            }}
          />
        )}
      </div>
    ),
    [originFilter, statusFilter, globalFilter, hasActiveFilters],
  );

  const isEmpty = !loading && flatData.length === 0;
  const isPlatformEmpty = isEmpty && originFilter === "PLATFORM";

  return (
    <div className="overflow-hidden">
      <GenericDetailsModal
        visible={isDetailsModalOpen}
        onHide={handleCloseModal}
        sections={detailLoading ? [] : sections}
        header={T.MODAL_HEADER}
        noDataText={detailLoading ? "Loading contract details..." : T.NO_DATA}
      />

      {isPlatformEmpty ? (
        <>
          <div className="flex justify-content-between align-items-center mb-2">
            <h2 className="text-xl font-semibold text-primary">
              Contracts & Payments
            </h2>
          </div>
          <div className="mb-2">{filterBar}</div>
          <div
            className="flex flex-column align-items-center justify-content-center text-center py-8 surface-card border-round shadow-1"
            style={{ minHeight: "400px" }}
          >
            <i
              className="pi pi-chart-line text-primary mb-3"
              style={{ fontSize: "3rem", opacity: 0.6 }}
            />
            <h3 className="text-xl font-semibold text-900 mt-0 mb-2">
              No platform contracts yet
            </h3>
            <p
              className="text-color-secondary mt-0 mb-4 line-height-3"
              style={{ maxWidth: "420px" }}
            >
              Contracts created through your website (move-ins and reservations)
              will appear here automatically. You can view all Storage Commander
              contracts in the meantime.
            </p>
            <Button
              label="View All Contracts"
              icon="pi pi-list"
              className="p-button-outlined"
              onClick={() => setOriginFilter("")}
            />
          </div>
        </>
      ) : (
        <Table
          columns={COLUMNS}
          data={flatData}
          title="Contracts & Payments"
          onRowClicked={handleRowClick}
          loading={loading}
          loadMore={hasMore ? loadMore : undefined}
          extraHeader={filterBar}
        />
      )}
    </div>
  );
}
